import { useTranslation } from "react-i18next";
import { LogOut } from "lucide-react";

import { Button } from "@/components/ui/button";
import { PATHS } from "@/routes/paths";

import { AccountDetails } from "./components/AccountDetails";
import { CompaniesSection } from "./components/CompaniesSection";
import { ProfileDrawerHeader } from "./components/ProfileDrawerHeader";
import { ProfileDrawerSkeleton } from "./components/ProfileDrawerSkeleton";
import { ProfileIdentityCard } from "./components/ProfileIdentityCard";
import { SettingsLinks } from "./components/SettingsLinks";
import { useProfileDrawer } from "./use-profile-drawer";
import { TimeSavedCard } from "../time-saved-card";
import { TokenUpgradeWidget } from "../token-upgrade-widget";

interface ProfileDrawerProps {
  /** Closes the owning `Drawer`. */
  onClose: () => void;
  /** Opens the sign-out confirmation dialog, which lives in `ProfileMenu`. */
  onRequestSignOut: () => void;
}

/**
 * Per-section entrance offset. Sections rise in top-to-bottom rather than all
 * at once, which reads as the panel assembling itself. Disabled wholesale under
 * `prefers-reduced-motion` (see `.animate-rise-in` in `global.css`).
 */
const stagger = (index: number) => ({ animationDelay: `${index * 70}ms` });

/**
 * Contents of the header avatar's drawer.
 *
 * Layout is three fixed zones — top bar, one scroll region, pinned footer — so
 * the destructive Sign Out action is always reachable and never buried under a
 * long companies list. The owning `Drawer` therefore hands over its body
 * padding and scrolling (see `ProfileMenu`).
 *
 * Reads the user straight from the store rather than taking it as a prop:
 * there is exactly one signed-in user, and threading it through added a second
 * source of truth alongside the `isAdmin` flag this tree already selects.
 */
export const ProfileDrawer = ({
  onClose,
  onRequestSignOut,
}: ProfileDrawerProps) => {
  const { t } = useTranslation(["common"]);
  const { user, details, quickLinks, subscription, goTo } =
    useProfileDrawer(onClose);
  const {
    isAdmin,
    hasSubscription,
    hasSubscriptionAccess,
    totalTimeSavedMinutes,
    tokenBalance,
    isTokensLoading,
  } = subscription;

  const canSeeTokenWidget = isAdmin && hasSubscription && hasSubscriptionAccess;

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <ProfileDrawerHeader
        onEdit={() => goTo(PATHS.MY_SPACE.MS_SETTINGS_PATHS.TAB_PROFILE)}
        onClose={onClose}
      />

      <div className="scrollbar-thin flex-1 space-y-5 overflow-y-auto px-4 pb-4">
        {!user ? (
          <ProfileDrawerSkeleton />
        ) : (
          <>
            <div className="animate-rise-in" style={stagger(0)}>
              <ProfileIdentityCard user={user}>
                {/* Mobile only — the desktop header shows these inline. */}
                {hasSubscription && (
                  <TimeSavedCard
                    totalTimeSavedMinutes={totalTimeSavedMinutes}
                  />
                )}
                {canSeeTokenWidget && (
                  <TokenUpgradeWidget
                    variant="card"
                    className="w-full"
                    balance={tokenBalance}
                    isLoading={isTokensLoading}
                    onUpgrade={() =>
                      goTo(PATHS.STANDALONE_PATHS.SUBSCRIPTION_TOKENS)
                    }
                  />
                )}
              </ProfileIdentityCard>
            </div>

            <div className="animate-rise-in" style={stagger(1)}>
              <AccountDetails details={details} />
            </div>
            <div className="animate-rise-in" style={stagger(2)}>
              <SettingsLinks links={quickLinks} onNavigate={goTo} />
            </div>
            <div className="animate-rise-in" style={stagger(3)}>
              <CompaniesSection />
            </div>
          </>
        )}
      </div>

      {/* Glass footer — content scrolls under a blurred plate rather than
          stopping at a hard rule, and the hairline is a fading gradient. */}
      <footer className="relative shrink-0 bg-surface/80 p-4 backdrop-blur-md before:absolute before:inset-x-0 before:top-0 before:h-px before:bg-linear-to-r before:from-transparent before:via-border before:to-transparent">
        <Button
          variant="outline-danger"
          onClick={onRequestSignOut}
          className="group w-full transition-all hover:bg-danger hover:text-danger-foreground active:scale-[0.99]"
        >
          <LogOut
            size={15}
            aria-hidden
            className="transition-transform duration-200 group-hover:-translate-x-0.5"
          />
          {t("common:chrome.sign_out")}
        </Button>
      </footer>
    </div>
  );
};
